import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import Input from '__components/Kizen/Input';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import {
  StyledButtonsWrapper,
  StyledPageWrapper,
  StyledPaginationWrapper,
  StyledSearchInput,
  StyledTableScrollContainer,
  StyledTableToolbar,
  StyledTableWrapper,
} from 'pages/KnowledgeSettings/styles';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import {
  DEFAULT_PAGES_PAGINATION_SETTINGS,
  ONLINE_SOURCE_PAGES_TABLE_ACTIONS,
} from 'pages/KnowledgeSettings/constants';
import { useColumns } from './columns';
import { onlineSourcePagesSearchQuery } from 'query/knowledge';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { OnlineSourcePageListItem } from '@kizen/api/knowledge';
import { usePageConfig } from 'pages/KnowledgeSettings/hooks/usePageConfig';
import { useCatalogPermission } from 'pages/KnowledgeSettings/hooks/useCatalogPermission';
import Loader from '__components/Kizen/Loader';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import KnowledgeService from 'services/KnowledgeService';
import { Button } from '@kizen/kds/Button';
import { PageContentModal } from './PageContentModal';
import { QuickFilters } from './QuickFilters';

export const CatalogOnlineSourcePages = () => {
  const { t } = useTranslation();
  const [showToast, toastVariant] = useToast();
  const { catalogId } = useParams<{ catalogId: string }>();
  const history = useHistory<{ source?: string }>();

  const { canWrite } = useCatalogPermission(catalogId);

  const {
    paginationSettings: { page, pageSize, ordering, search, filters },
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
    pageIsReady,
    setPaginationSettings,
  } = usePageConfig({
    pageKey: 'knowledge_crawled_pages_list',
    defaultConfig: DEFAULT_PAGES_PAGINATION_SETTINGS,
  });

  useEffect(() => {
    const source = history.location.state?.source;
    if (source && pageIsReady) {
      setPaginationSettings((prev) => ({
        ...prev,
        page: 1,
        search: '',
        filters: {
          source,
          status: null,
        },
      }));

      history.replace(history.location.pathname, { source: undefined });
    }
  }, [history, pageIsReady, setPaginationSettings]);

  const [pageId, setPageId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const queryOptions = useMemo(() => {
    return onlineSourcePagesSearchQuery({
      params: {
        page,
        page_size: pageSize,
        ordering,
        search,
        catalog_id: catalogId,
        crawl_config_id: filters?.source || undefined,
        ...(filters?.status && {
          is_active: filters.status
            ? filters.status !== 'disabled_by_source'
            : undefined,
          is_active_by_user:
            filters.status !== 'disabled_by_source'
              ? filters.status === 'active'
              : undefined,
        }),
      },
      placeholderData: keepPreviousData,
      staleTime: 0,
      enabled: pageIsReady && !history.location.state?.source,
      refetchOnWindowFocus: false,
    });
  }, [
    pageSize,
    page,
    ordering,
    search,
    catalogId,
    pageIsReady,
    history.location.state?.source,
    filters,
  ]);

  const { data, isLoading, isFetching } = useQuery(queryOptions);

  const loading = isLoading || isFetching;

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
  }, [queryClient, queryOptions.queryKey]);

  const onSelectAction = useCallback(
    async (
      { value }: { value: keyof typeof ONLINE_SOURCE_PAGES_TABLE_ACTIONS },
      data: OnlineSourcePageListItem
    ) => {
      if (value === ONLINE_SOURCE_PAGES_TABLE_ACTIONS.view) {
        setPageId(data.id);
      }
      if (
        value === ONLINE_SOURCE_PAGES_TABLE_ACTIONS.deactivate ||
        value === ONLINE_SOURCE_PAGES_TABLE_ACTIONS.activate
      ) {
        try {
          await KnowledgeService.setOnlineSourcePageActiveStatus(
            data.knowledge_source.id,
            value === ONLINE_SOURCE_PAGES_TABLE_ACTIONS.activate
          );
          showToast({
            message: t('Online Source Page {{ action }} successfully', {
              action:
                value === ONLINE_SOURCE_PAGES_TABLE_ACTIONS.activate
                  ? t('activated')
                  : t('deactivated'),
            }),
            variant: toastVariant.SUCCESS,
          });
          onRefresh();
        } catch (error) {
          const message =
            getErrorMessage(getOriginalError(error)) ||
            t('Failed to update Online Source');
          showToast({
            message,
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [onRefresh, showToast, t, toastVariant]
  );

  const onHidePageContentModal = useCallback(() => setPageId(null), []);

  const columns = useColumns({ onSelectAction, canWrite });

  return (
    <Loader loading={!pageIsReady}>
      <StyledPageWrapper>
        <StyledTableToolbar>
          <StyledSearchInput
            pill
            type={Input.INPUT_TYPES.SEARCH}
            placeholder={t('Find Pages')}
            value={actualSearch}
            onChange={handleChangeSearch}
            data-qa-id="catalog-online-source-pages-search"
          />
          <KizenTypography as="h2" type="header">
            {t('{{count}} Online Knowledge Source {{instance}}', {
              count: data?.count || 0,
              instance: data?.count === 1 ? t('Page') : t('Pages'),
            })}
          </KizenTypography>
          <StyledButtonsWrapper>
            <div style={{ width: 100 }} />
            <Button
              leftIcon="action-refresh"
              variant="text"
              onClick={onRefresh}
              color={loading ? 'inherit' : 'secondary'}
              disabled={loading}
            />
          </StyledButtonsWrapper>
        </StyledTableToolbar>
        <StyledTableToolbar>
          <QuickFilters
            filters={filters}
            setFilters={setPaginationSettings}
            catalogId={catalogId}
          />
        </StyledTableToolbar>
        <StyledTableWrapper>
          <StyledTableScrollContainer bottom top gradient={<Gradient />}>
            <BasicTable
              stickyHeader
              head={<TRow head columns={columns} data={headData} />}
              staleData={loading}
            >
              {data?.count ? null : <TRow />}
              {data?.results.map((page: any) => (
                <TRow key={page.id} columns={columns} data={page} />
              ))}
            </BasicTable>
          </StyledTableScrollContainer>
        </StyledTableWrapper>
        <StyledPaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={data?.count}
            onChangePage={handleChangePage}
            onChangePerPage={handleChangePageSize}
          />
        </StyledPaginationWrapper>
      </StyledPageWrapper>
      {pageId ? (
        <PageContentModal pageId={pageId} catalogId={catalogId} onHide={onHidePageContentModal} />
      ) : null}
    </Loader>
  );
};
